import { useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, FileUp, ArrowRight, ArrowLeft, CheckCircle2, Upload, Sparkles, AlertTriangle } from "lucide-react";

const FIELDS = [
  { key: "title", label: "Task name", required: true },
  { key: "start_date", label: "Start date" },
  { key: "end_date", label: "End / finish date" },
  { key: "percent_complete", label: "% complete" },
  { key: "assignee", label: "Assignee / resource" },
  { key: "phase", label: "Phase / group" },
  { key: "is_milestone", label: "Milestone flag" },
  { key: "predecessors", label: "Predecessors (row #s)" },
  { key: "notes", label: "Notes" },
];

export default function ImportWizard({ open, onOpenChange, projectId, onImported }) {
  const [step, setStep] = useState(0);
  const [parsing, setParsing] = useState(false);
  const [parsed, setParsed] = useState(null);
  const [mapping, setMapping] = useState({});
  const [mode, setMode] = useState("append");
  const [committing, setCommitting] = useState(false);
  const [result, setResult] = useState(null);
  const [cleaning, setCleaning] = useState(false);
  const [cleanup, setCleanup] = useState(null);
  const fileRef = useRef(null);

  const reset = () => { setStep(0); setParsed(null); setMapping({}); setMode("append"); setResult(null); setCleanup(null); };

  const runCleanup = async () => {
    setCleaning(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/import/cleanup`);
      setCleanup(data);
      toast.success("AI clean-up complete");
      onImported?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Clean-up failed");
    } finally {
      setCleaning(false);
    }
  };

  const parse = async (file) => {
    if (!file) return;
    setParsing(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/projects/${projectId}/import/parse`, fd);
      setParsed(data);
      setMapping(data.suggested_mapping);
      setStep(1);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not read the file");
    } finally {
      setParsing(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const commit = async () => {
    setCommitting(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/import/commit`, { mapping, rows: parsed.rows, mode });
      setResult(data);
      setStep(3);
      onImported?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Import failed");
    } finally {
      setCommitting(false);
    }
  };

  const colName = (i) => (i >= 0 && parsed ? parsed.columns[i] : null);
  const previewRows = parsed ? parsed.rows.slice(0, 5) : [];

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="max-w-2xl" data-testid="import-wizard">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><FileUp className="h-5 w-5 text-primary" /> Import plan {parsed ? `— ${parsed.row_count} rows` : ""}</DialogTitle>
        </DialogHeader>

        {step === 0 && (
          <div className="space-y-4">
            <div
              className="border-2 border-dashed border-border rounded-lg p-10 text-center cursor-pointer hover:border-primary/50 transition-colors"
              onClick={() => fileRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); parse(e.dataTransfer.files[0]); }}
              data-testid="import-dropzone"
            >
              {parsing ? <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" /> : <Upload className="h-8 w-8 text-primary mx-auto" />}
              <p className="font-semibold text-sm mt-3">{parsing ? "Reading file…" : "Drop your file here or click to browse"}</p>
              <p className="text-xs text-muted-foreground mt-1.5">Excel (.xlsx), CSV, or MS Project saved as XML<br />(In MS Project: File → Save As → XML format)</p>
            </div>
            <input ref={fileRef} type="file" accept=".xlsx,.xlsm,.csv,.xml" className="hidden" onChange={(e) => parse(e.target.files[0])} data-testid="import-file-input" />
          </div>
        )}

        {step === 1 && parsed && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">Match your file's columns to plan fields. We've guessed where we could.</p>
            <div className="grid sm:grid-cols-2 gap-2.5 max-h-[45vh] overflow-y-auto pr-1">
              {FIELDS.map((f) => (
                <div key={f.key} className="flex items-center gap-2">
                  <span className={`text-xs w-36 shrink-0 ${f.required ? "font-bold" : "text-muted-foreground"}`}>{f.label}{f.required && " *"}</span>
                  <Select value={String(mapping[f.key] ?? -1)} onValueChange={(v) => setMapping((mm) => ({ ...mm, [f.key]: Number(v) }))}>
                    <SelectTrigger className="h-8 text-xs flex-1" data-testid={`import-map-${f.key}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="-1">— not in file —</SelectItem>
                      {parsed.columns.map((c, i) => <SelectItem key={i} value={String(i)}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
            <div className="flex justify-between">
              <Button variant="ghost" size="sm" className="gap-1" onClick={() => { setStep(0); setParsed(null); }} data-testid="import-back-0"><ArrowLeft className="h-3.5 w-3.5" /> Back</Button>
              <Button size="sm" className="gap-1" disabled={(mapping.title ?? -1) < 0} onClick={() => setStep(2)} data-testid="import-to-preview">Preview <ArrowRight className="h-3.5 w-3.5" /></Button>
            </div>
          </div>
        )}

        {step === 2 && parsed && (
          <div className="space-y-4">
            <div className="border border-border rounded-lg overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-secondary/50">
                  <tr>
                    {FIELDS.filter((f) => (mapping[f.key] ?? -1) >= 0).map((f) => <th key={f.key} className="px-2.5 py-2 text-left font-semibold whitespace-nowrap">{f.label}</th>)}
                  </tr>
                </thead>
                <tbody data-testid="import-preview">
                  {previewRows.map((r, ri) => (
                    <tr key={ri} className="border-t border-border">
                      {FIELDS.filter((f) => (mapping[f.key] ?? -1) >= 0).map((f) => (
                        <td key={f.key} className="px-2.5 py-1.5 max-w-[180px] truncate">{r[mapping[f.key]]}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-muted-foreground">Showing first {previewRows.length} of {parsed.row_count} rows. Rows without a task name are skipped.</p>
            <div className="flex items-center gap-3">
              <span className="text-xs font-semibold">Import mode</span>
              <Select value={mode} onValueChange={setMode}>
                <SelectTrigger className="h-8 w-56 text-xs" data-testid="import-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="append">Add to existing tasks</SelectItem>
                  <SelectItem value="replace">Replace all existing tasks</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {mode === "replace" && <p className="text-xs text-red-500 font-semibold">⚠ This deletes every current task in the project first.</p>}
            <div className="flex justify-between">
              <Button variant="ghost" size="sm" className="gap-1" onClick={() => setStep(1)} data-testid="import-back-1"><ArrowLeft className="h-3.5 w-3.5" /> Back</Button>
              <Button size="sm" className="gap-1" onClick={commit} disabled={committing} data-testid="import-commit">
                {committing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileUp className="h-3.5 w-3.5" />} Import {parsed.row_count} rows
              </Button>
            </div>
          </div>
        )}

        {step === 3 && result && (
          <div className="py-4 space-y-4" data-testid="import-result">
            <div className="text-center space-y-2">
              <CheckCircle2 className="h-10 w-10 text-emerald-500 mx-auto" />
              <p className="font-display font-bold text-lg">{result.created} tasks imported</p>
              <p className="text-sm text-muted-foreground">
                {result.skipped > 0 && `${result.skipped} row(s) skipped (no task name). `}
                {result.dependencies_linked > 0 && `${result.dependencies_linked} dependency link(s) created.`}
              </p>
            </div>
            {!cleanup ? (
              <div className="border border-primary/30 bg-primary/5 rounded-lg p-4 text-center space-y-2">
                <p className="text-sm">Want AI to organize phases, fill missing dates from dependencies, and flag suspicious rows?</p>
                <Button className="gap-2" onClick={runCleanup} disabled={cleaning} data-testid="import-cleanup">
                  {cleaning ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} {cleaning ? "Cleaning up…" : "Clean up with AI"}
                </Button>
              </div>
            ) : (
              <div className="border border-border rounded-lg p-4 space-y-2" data-testid="import-cleanup-result">
                <p className="text-sm font-semibold flex items-center gap-2"><Sparkles className="h-4 w-4 text-primary" /> Clean-up done — {cleanup.phases_updated} phase(s) assigned, {cleanup.dates_filled} date(s) filled</p>
                {cleanup.flags.length > 0 && (
                  <div className="space-y-1 max-h-40 overflow-y-auto">
                    {cleanup.flags.map((f, i) => (
                      <div key={i} className="flex items-start gap-2 text-xs" data-testid={`import-flag-${i}`}>
                        <AlertTriangle className="h-3.5 w-3.5 text-amber-500 shrink-0 mt-0.5" />
                        <span><strong>{f.title}</strong> — {f.reason}</span>
                      </div>
                    ))}
                  </div>
                )}
                {cleanup.flags.length === 0 && <p className="text-xs text-muted-foreground">No suspicious rows found.</p>}
              </div>
            )}
            <div className="flex justify-center">
              <Button variant={cleanup ? "default" : "outline"} onClick={() => { onOpenChange(false); reset(); }} data-testid="import-done">Done</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
